import * as React from 'react'
import { X, Package, Pencil } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { SearchProductInput } from './search-product-input'
import { SearchProductOutputItem, useSearchProducts } from '@/lib/api/products'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from "@/lib/utils"

interface ProductSelectorProps {
    storeId: string
    productId?: string
    onProductChange: (productId: string) => void
    error?: string
    label?: string
    required?: boolean
    helpText?: string
    /** Optional element rendered next to the search input (hidden once a product is selected). */
    action?: React.ReactNode
    /** When provided, shows an edit (pencil) button on the selected product. */
    onEdit?: (productId: string) => void
}

const getProductImage = (product: SearchProductOutputItem): string | null => {
    const defaultVariant = product.variants.find((v) => v.default)
    return defaultVariant?.imageUrl ?? product.variants[0]?.imageUrl ?? null
}

/**
 * Component for selecting a single product with search functionality
 */
export const ProductSelector = ({
    storeId,
    productId,
    onProductChange,
    error,
    label,
    required = false,
    helpText,
    action,
    onEdit,
}: ProductSelectorProps) => {
    const { t } = useTranslation(['stores', 'common'])
    // Cache resolved product details by id; the displayed product is derived
    // strictly from the `productId` prop so it stays in sync (incl. removal).
    const [productCache, setProductCache] = React.useState<Record<string, SearchProductOutputItem>>({})

    const { data: searchData, isLoading } = useSearchProducts({
        storeId,
        productId: productId || null,
        searchValue: null,
    })

    React.useEffect(() => {
        if (productId && searchData) {
            const product = searchData.find((p) => p.id === productId)
            if (product) {
                // Overwrite so an edited product's details refresh after refetch.
                setProductCache((prev) => ({ ...prev, [product.id]: product }))
            }
        }
    }, [productId, searchData])

    const selectedProduct = productId ? productCache[productId] ?? null : null

    // A product is set but its details aren't resolved yet — show a skeleton
    // instead of flashing the empty search input.
    const resolving = !!productId && !selectedProduct && (isLoading || !searchData)

    const handleProductSelect = (product: SearchProductOutputItem) => {
        setProductCache((prev) => ({ ...prev, [product.id]: product }))
        onProductChange(product.id)
    }

    const handleProductRemove = () => {
        onProductChange('')
    }

    return (
        <div className="space-y-3">
            {resolving ? (
                <div className="space-y-2">
                    {label && (
                        <label className="text-sm font-medium leading-none">
                            {label} {required && '*'}
                        </label>
                    )}
                    <Skeleton className="h-[68px] w-full rounded-md" />
                </div>
            ) : !selectedProduct ? (
                <div className="space-y-2">
                    {label && (
                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {label} {required && '*'}
                        </label>
                    )}
                    <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                            <SearchProductInput
                                storeId={storeId}
                                onSelect={handleProductSelect}
                            />
                        </div>
                        {action}
                    </div>
                    {error && (
                        <p className="text-xs leading-tight text-red-600">
                            {error}
                        </p>
                    )}
                    {helpText && (
                        <p className="text-muted-foreground text-xs">
                            {helpText}
                        </p>
                    )}
                </div>
            ) : (
                <div className="space-y-2">
                    {label && (
                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {label} {required && '*'}
                        </label>
                    )}
                    <div
                        className={cn(
                            'flex items-center gap-3 p-3 rounded-md border bg-card'
                        )}
                    >
                        <div className="flex-shrink-0 w-12 h-12 bg-muted rounded overflow-hidden flex items-center justify-center">
                            {getProductImage(selectedProduct) ? (
                                <img
                                    src={getProductImage(selectedProduct)!}
                                    alt={selectedProduct.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <Package className="w-6 h-6 text-muted-foreground" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{selectedProduct.name}</p>
                            {selectedProduct.description && (
                                <p className="text-xs text-muted-foreground truncate">
                                    {selectedProduct.description}
                                </p>
                            )}
                        </div>
                        <Badge variant="secondary" className="flex-shrink-0">
                            {selectedProduct.variants.length} {selectedProduct.variants.length === 1 ? 'variant' : 'variants'}
                        </Badge>
                        {onEdit && productId && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => onEdit(productId)}
                                className="flex-shrink-0 h-8 w-8 p-0"
                                title={t('common:edit')}
                            >
                                <Pencil className="h-4 w-4" />
                                <span className="sr-only">{t('common:edit')}</span>
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleProductRemove}
                            className="flex-shrink-0 h-8 w-8 p-0"
                            title={t('common:remove')}
                        >
                            <X className="h-4 w-4" />
                            <span className="sr-only">{t('common:remove')}</span>
                        </Button>
                    </div>
                    {error && (
                        <p className="text-xs leading-tight text-red-600">
                            {error}
                        </p>
                    )}
                    {helpText && (
                        <p className="text-muted-foreground text-xs">
                            {helpText}
                        </p>
                    )}
                </div>
            )}
        </div>
    )
}

